'use client';
import { useState, useEffect } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import Dropdown from 'react-bootstrap/Dropdown';
import styles from '@/components/grid-post/GridPost.module.css';
import Slugify from '@/components/Slugify';

const BlogFiltersClient = ({
    categories,
    tags,
    selectedCategory,
    selectedTag,
    selectedOrder,
    locale,
    customClass,
    customClass2
}) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    // UI States
    const [isVisible, setIsVisible] = useState(false);
    const [panelTags, setPanelTags] = useState(false);

    const updateFilters = (key, value) => {
        const params = new URLSearchParams(searchParams);

        if (value && value !== 'Todas las categorias' && value !== 'Filtrar por etiqueta') {
            params.set(key, value);
        } else {
            params.delete(key);
        }

        // Always reset to page 1 on filter change
        params.set('page', '1');

        router.push(`${pathname}?${params.toString()}`, { scroll: false });

        // Manual scroll if needed
        const target = document.getElementById("blogPosts");
        if (target) {
            const y = target.getBoundingClientRect().top + window.scrollY - 200;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    const handleCategoryClick = (category) => {
        updateFilters('category', category);
    };

    const handleTagSelect = (tag) => {
        updateFilters('tag', tag);
        handleClosePanelTags();
    };

    const handleOrderChange = (e) => {
        const newOrder = e.target.value;
        const params = new URLSearchParams(searchParams);
        params.set('order', newOrder);
        params.set('page', '1');
        router.push(`${pathname}?${params.toString()}`, { scroll: false });
    };

    const handlePanelTags = () => {
        setPanelTags(true);
        document.body.classList.add('open_tags');
    };

    const handleClosePanelTags = () => {
        setPanelTags(false);
        document.body.classList.remove('open_tags');
    };

    // Sticky Header Logic
    useEffect(() => {
        const checkVisibility = () => {
            const element = document.getElementById('filterCategories');
            if (element) {
                const rect = element.getBoundingClientRect();
                setIsVisible(
                    rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
                );
            }
        };

        window.addEventListener('scroll', checkVisibility);
        window.addEventListener('resize', checkVisibility);
        checkVisibility();

        return () => {
            window.removeEventListener('scroll', checkVisibility);
            window.removeEventListener('resize', checkVisibility);
        };
    }, []);

    useEffect(() => {
        if (isVisible) {
            document.body.classList.remove('categorias-hidden');
        } else {
            document.body.classList.add('categorias-hidden');
        }
    }, [isVisible]);

    return (
        <>
            {/* Mobile Categories Dropdown */}
            <div id="filterCatetories" className={`d-block d-sm-flex d-md-none ${styles.filterCategories} ${styles.filterCategories__mobile} ${customClass}`}>
                <Dropdown onSelect={handleCategoryClick} className={styles.Dropdown}>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                        <i className="bi bi-filter d-none"></i>
                        <span>{selectedCategory}</span>
                        <i className="bi bi-chevron-down"></i>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className={styles.DropdownToggle}>
                        <Dropdown.Item eventKey="Todas las categorias" className={`dopdown_item__${Slugify('Todas las categorias')}`} >
                            {locale === 'es' ? 'Todas las categorias' : 'All categories'}
                        </Dropdown.Item>
                        {categories.map((category, index) => (
                            <Dropdown.Item key={index} eventKey={category.name} className={`dopdown_item__${Slugify(category.name)}`} >
                                {category.name}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <div className={`${styles.orderSelectors} mb-xxl gap-5 ${customClass2}`}>
                {tags && tags.length > 0 && (
                    <div className={`${styles.drop} drop`}>
                        <div className="d-none">
                            <button type="button" className={styles.Dropdown} onClick={handlePanelTags}>
                                <i className="bi bi-filter d-block d-sm-block d-md-none"></i>
                                <span>{locale == 'es' ? 'Filtrar por etiqueta' : 'Filter by tag'}</span>
                                {selectedTag !== 'Filtrar por etiqueta' && (
                                    <span className={styles.selected__numb}>1</span>
                                )}
                                <i className="bi bi-chevron-down d-none d-sm-none d-md-block"></i>
                            </button>

                            <div className={`${styles.panel__tags} ${panelTags ? styles.panelTagsActive : ''}`}>
                                <div className={styles.header__panel} >
                                    <p>{locale == 'es' ? 'Filtrar por etiqueta' : 'Filter by tag'}</p>
                                    <i className="bi bi-x-lg" onClick={handleClosePanelTags}></i>
                                </div>
                                <div className={styles.subheader__panel}>
                                    <p>{locale == 'es' ? 'Selecciona una opción' : 'Select a option'}</p>
                                </div>
                                <div className={styles.body__panel}>
                                    {tags.map((tag, index) => (
                                        <div key={index} className={styles.item__tag}>
                                            <input type="radio" name="tag" id={`tag-${index + 1}`} className="panel__tag-input" onChange={() => handleTagSelect(tag.name)} checked={selectedTag === tag.name} />
                                            <label htmlFor={`tag-${index + 1}`} className="panel__tag-label">{tag.name}</label>
                                        </div>
                                    ))}
                                </div>
                                {selectedTag !== 'Filtrar por etiqueta' && (
                                    <div className={styles.footer__panel}>
                                        <button onClick={handleClosePanelTags} className="evanhub-btn mx-w-250 btn-full__primary">{locale == 'es' ? 'Mostrar resultados' : 'Show results'}</button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="">
                            <Dropdown onSelect={handleTagSelect} className={styles.Dropdown}>
                                <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                                    <i className="bi bi-filter d-none"></i>
                                    <span>{selectedTag}</span>
                                    <i className="bi bi-chevron-down"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu className={styles.DropdownToggle}>
                                    <Dropdown.Item eventKey="Filtrar por etiqueta">
                                        {locale == 'es' ? 'Todas' : 'All'}
                                    </Dropdown.Item>
                                    {tags.map((tag, index) => (
                                        <Dropdown.Item key={index} eventKey={tag.name} className={`dopdown_item__${Slugify(tag.name)}`} >
                                            {tag.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                )}

                <div className={styles.selectorDate}>
                    <span>{locale === 'es' ? 'ordenar por:' : 'order by:'}</span>
                    <select onChange={handleOrderChange} value={selectedOrder}>
                        <option value="newest">{locale === 'es' ? 'Más recientes' : 'Newest'}</option>
                        <option value="oldest">{locale === 'es' ? 'Más antiguas' : 'Oldest'}</option>
                    </select>
                </div>
            </div>
        </>
    );
};

export default BlogFiltersClient;
